import { useState } from "react";
import { Plus } from "lucide-react";
import { useSetPageHeader } from "@/lib/page-header";
import { useTourDaPagina } from "@/components/product-tour/PageTourEntry";
import { ESTOQUE_TOUR_STEPS } from "@/lib/product-tour/page-tour-steps";
import { usePermissao } from "@/lib/use-permissao";
import { useParametroDaUrl } from "@/lib/use-parametro-da-url";
import { cn } from "@/lib/cn";
import { NoAccessBlock } from "@/components/shared/NoAccessBlock";
import { SugestaoDeCompra } from "@/components/estoque/SugestaoDeCompra";
import { SaldosPorMaterial } from "@/components/estoque/SaldosPorMaterial";
import { ExtratoDeEstoque } from "@/components/estoque/ExtratoDeEstoque";
import { RegistrarCompraDialog } from "@/components/estoque/RegistrarCompraDialog";
import { InformarSaldoDialog } from "@/components/estoque/InformarSaldoDialog";
import { InsumosSection } from "@/components/estoque/InsumosSection";

const cardCls = "rounded-xl border border-zinc-200 bg-white p-5 shadow-sm dark:border-border dark:bg-card";

type Aba = "comprar" | "saldos" | "movimentos" | "insumos";

const ABAS: [Aba, string][] = [
  ["comprar", "Comprar"],
  ["saldos", "Saldos"],
  ["movimentos", "Movimentos"],
  // O cadastro veio do catálogo em 30/09: preço de compra, fornecedor, unidade e mínimo são
  // campos de estoque, e mantê-los no catálogo misturava o que se vende com o que se compra.
  ["insumos", "Insumos"],
];

/**
 * Estoque do laboratório.
 *
 * A ordem das abas é a ordem de utilidade, e não é acidente. A primeira pergunta que o laboratório
 * tem é *o que comprar* — e ela se responde com o consumo dos serviços concluídos, sem exigir que
 * alguém conte a prateleira antes. Saldo e extrato vêm depois porque dependem de inventário, que é
 * exatamente a etapa onde o controle em planilha costuma morrer.
 */
export function Estoque() {
  const pode = usePermissao("stock.manage");
  const [aba, setAba] = useState<Aba>("comprar");
  useParametroDaUrl("aba", (v) => {
    if (v === "comprar" || v === "saldos" || v === "insumos" || v === "movimentos") setAba(v);
  });
  const [comprando, setComprando] = useState<{ id: string; sugerido: number } | null>(null);
  const [compraAberta, setCompraAberta] = useState(false);
  const [contando, setContando] = useState<string | null>(null);
  const [extratoDe, setExtratoDe] = useState<string | null>(null);

  useSetPageHeader({
    title: "Estoque",
    subtitle: "O que comprar, quanto há, o que já saiu e o cadastro dos insumos",
  });

  useTourDaPagina(pode ? ESTOQUE_TOUR_STEPS : []);

  if (!pode) {
    return <NoAccessBlock description="O estoque é restrito a quem tem a permissão de estoque nesta organização." />;
  }

  return (
    <div className="mx-auto w-full max-w-[1400px]">
      <div className={cardCls}>
        {/* A ação principal da tela fica colada nas abas: é a mesma faixa de navegação, e registrar
            entrada vale de qualquer aba. No celular desce pra baixo das abas. */}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <div data-tour="estoque-abas" className="flex flex-1 gap-1 rounded-lg border border-zinc-200 bg-zinc-50 p-1 dark:border-border dark:bg-muted/40">
            {ABAS.map(([chave, rotulo]) => (
              <button
                key={chave}
                type="button"
                onClick={() => setAba(chave)}
                className={cn(
                  "h-8 flex-1 rounded-md text-xs font-semibold transition-colors",
                  aba === chave
                    ? "bg-brand-600 text-white shadow-sm"
                    : "text-zinc-600 hover:text-zinc-900 dark:text-muted-foreground dark:hover:text-foreground",
                )}
              >
                {rotulo}
              </button>
            ))}
          </div>
          <button
            type="button"
            data-tour="estoque-registrar-entrada"
            onClick={() => {
              setComprando(null);
              setCompraAberta(true);
            }}
            className="inline-flex h-10 shrink-0 items-center justify-center gap-1.5 rounded-md bg-brand-600 px-4 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-brand-700"
          >
            <Plus className="h-3.5 w-3.5" />
            Registrar entrada
          </button>
        </div>

        <div className="mt-4">
          {aba === "comprar" && (
            <SugestaoDeCompra
              onComprar={(id, sugerido) => {
                setComprando({ id, sugerido });
                setCompraAberta(true);
              }}
            />
          )}
          {aba === "saldos" && (
            <SaldosPorMaterial
              onInformar={(id) => setContando(id)}
              onExtrato={(id) => {
                setExtratoDe(id);
                setAba("movimentos");
              }}
            />
          )}
          {aba === "insumos" && <InsumosSection />}
          {aba === "movimentos" &&
            (extratoDe ? (
              <ExtratoDeEstoque materialLineId={extratoDe} />
            ) : (
              <p className="py-10 text-center text-xs text-zinc-400 dark:text-muted-foreground">
                Escolha um insumo em <strong>Saldos</strong> para ver os lançamentos dele.
              </p>
            ))}
        </div>
      </div>

      <RegistrarCompraDialog
        aberto={compraAberta}
        materialInicial={comprando}
        onFechar={() => {
          setCompraAberta(false);
          setComprando(null);
        }}
      />
      <InformarSaldoDialog materialLineId={contando} onFechar={() => setContando(null)} />
    </div>
  );
}
